import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { generateRAGChatResponse, hasDocuments } from '../../services/geminiChatService';

/**
 * AI Chatbot Component
 * Floating chatbot accessible from all dashboards with role-based context
 * 
 * @param {Object} props
 * @param {string} props.userRole - User role (student, pcf_admin, city_official)
 * @param {string} props.userId - Optional user ID for personalization
 */
const AIChatbot = ({ userRole = 'student', userId = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [streamingMessage, setStreamingMessage] = useState('');
  const [hasKnowledgeBase, setHasKnowledgeBase] = useState(false);
  
  const messagesEndRef = useRef(null);
  const chatHistoryRef = useRef([]);
  const modalRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage]);

  // Initialize with welcome message
  useEffect(() => {
    if (isOpen && messages?.length === 0) {
      const welcomeMessages = {
        student: 'Hello! I\'m your AI assistant for the Smart Cities Challenge. How can I help you today?',
        pcf_admin: 'Hello, Administrator! I\'m here to assist you with platform management and analytics. What would you like to know?',
        city_official: 'Hello! I\'m your AI assistant for the Smart Cities Challenge. I can help you with problem statements, team progress, and more.'
      };
      
      setMessages([{
        id: Date.now(),
        type: 'bot',
        text: welcomeMessages?.[userRole] || welcomeMessages?.student,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, userRole]);

  // Check if documents exist in knowledge base
  useEffect(() => {
    const checkDocuments = async () => {
      const documentsExist = await hasDocuments();
      setHasKnowledgeBase(documentsExist);
    };
    
    if (isOpen) {
      checkDocuments();
    }
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!inputMessage?.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError(null);
    setStreamingMessage('');

    try {
      // Create placeholder for streaming response
      const botMessageId = Date.now() + 1;
      let completeResponse = '';
      
      // Use RAG-based response generation
      await generateRAGChatResponse(
        inputMessage,
        userRole,
        (chunk) => {
          setStreamingMessage(prev => prev + chunk);
          completeResponse += chunk;
        },
        chatHistoryRef?.current
      );

      // Update chat history for context
      chatHistoryRef.current = [
        ...chatHistoryRef?.current,
        { role: 'user', parts: [{ text: inputMessage }] },
        { role: 'model', parts: [{ text: completeResponse }] }
      ];

      // Keep only last 10 messages in history to avoid token limits
      if (chatHistoryRef?.current?.length > 20) {
        chatHistoryRef.current = chatHistoryRef?.current?.slice(-20);
      }

      // Add complete message
      setMessages(prev => [...prev, {
        id: botMessageId,
        type: 'bot',
        text: completeResponse,
        timestamp: new Date()
      }]);

      setStreamingMessage('');
    } catch (err) {
      console.error('Chat error:', err);
      setError(err?.message || 'Failed to get response. Please try again.');
      
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'bot',
        text: 'I apologize, but I encountered an error. Please try again.',
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const handleMouseDown = (e) => {
    if (e?.target?.closest('.chat-header')) {
      setIsDragging(true);
      setDragStart({
        x: e?.clientX - position?.x,
        y: e?.clientY - position?.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e?.clientX - dragStart?.x,
        y: e?.clientY - dragStart?.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  const clearChat = () => {
    setMessages([]);
    chatHistoryRef.current = [];
    setError(null);
    // Re-initialize with welcome message
    const welcomeMessages = {
      student: 'Hello! I\'m your AI assistant for the Smart Cities Challenge. How can I help you today?',
      pcf_admin: 'Hello, Administrator! I\'m here to assist you with platform management and analytics. What would you like to know?',
      city_official: 'Hello! I\'m your AI assistant for the Smart Cities Challenge. I can help you with problem statements, team progress, and more.'
    };
    
    setMessages([{
      id: Date.now(),
      type: 'bot',
      text: welcomeMessages?.[userRole] || welcomeMessages?.student,
      timestamp: new Date()
    }]);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      {/* Floating Chat Icon */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center z-50 hover:scale-110"
          aria-label="Open AI Chat"
        >
          <Icon name="MessageCircle" size={24} />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-success rounded-full animate-pulse"></span>
        </button>
      )}
      {/* Chat Modal */}
      {isOpen && (
        <div
          ref={modalRef}
          className="fixed z-50 bg-card border border-border rounded-lg shadow-2xl flex flex-col"
          style={{
            width: '380px',
            height: '550px',
            bottom: '24px',
            right: '24px',
            transform: isDragging ? `translate(${position?.x}px, ${position?.y}px)` : 'none',
            cursor: isDragging ? 'grabbing' : 'default'
          }}
        >
          {/* Chat Header */}
          <div
            className="chat-header bg-primary text-primary-foreground p-4 rounded-t-lg flex items-center justify-between cursor-grab active:cursor-grabbing"
            onMouseDown={handleMouseDown}
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-foreground/20 rounded-full flex items-center justify-center">
                <Icon name="Sparkles" size={20} />
              </div>
              <div>
                <h3 className="font-semibold">AI Assistant</h3>
                // <p className="text-xs opacity-90">
                //   Powered by Gemini {hasKnowledgeBase && '• Knowledge Base Active'}
                // </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={clearChat}
                className="p-1.5 hover:bg-primary-foreground/20 rounded transition-colors"
                aria-label="Clear chat"
              >
                <Icon name="RotateCcw" size={18} />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-primary-foreground/20 rounded transition-colors"
                aria-label="Close chat"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-background">
            {messages?.map((message) => (
              <div
                key={message?.id}
                className={`flex ${message?.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message?.type === 'user' ?'bg-primary text-primary-foreground'
                      : message?.isError
                      ? 'bg-error/10 text-error border border-error/20' :'bg-muted text-foreground'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message?.text}</p>
                  <p className="text-xs opacity-70 mt-1">
                    {formatTime(message?.timestamp)}
                  </p>
                </div>
              </div>
            ))}

            {/* Streaming Message */}
            {streamingMessage && (
              <div className="flex justify-start">
                <div className="max-w-[80%] rounded-lg p-3 bg-muted text-foreground">
                  <p className="text-sm whitespace-pre-wrap">{streamingMessage}</p>
                  <div className="flex items-center space-x-1 mt-2">
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}

            {/* Loading Indicator */}
            {isLoading && !streamingMessage && (
              <div className="flex justify-start">
                <div className="bg-muted text-foreground rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-error/10 border border-error/20 text-error rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertTriangle" size={16} />
                  <p className="text-sm">{error}</p>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-border bg-card">
            <div className="flex items-end space-x-2">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e?.target?.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 resize-none border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary min-h-[40px] max-h-[100px]"
                rows="1"
                disabled={isLoading}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputMessage?.trim() || isLoading}
                size="sm"
                className="shrink-0"
              >
                <Icon name="Send" size={18} />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              AI responses may not always be accurate
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatbot;